import dayjs, { Dayjs } from "dayjs"
import dayjs_plugin_timezone from "dayjs/plugin/timezone"
import dayjs_plugin_utc from "dayjs/plugin/utc"

import { BarChartSettings, getBarChartSettings } from "./getBarChartsSettings"

dayjs.extend(dayjs_plugin_utc)
dayjs.extend(dayjs_plugin_timezone)

export const dropDownOptions = [
  {
    value: "lastHour",
    label: "Last Hour",
  },
  {
    value: "last6Hours",
    label: "Last 6 Hours",
  },
  {
    value: "last24Hours",
    label: "Last 24 Hours",
  },
  {
    value: "yesterday",
    label: "Yesterday",
  },
  {
    value: "last3Days",
    label: "Last 3 Days",
  },
  {
    value: "last7Days",
    label: "Last 7 Days",
  },
  {
    value: "last30Days",
    label: "Last 30 Days",
  },
  {
    value: "custom",
    label: "Custom",
  },
]

export const ChartSettings = {
  lastHour: { ticksCount: 15, dataCountsDivider: 1 },
  last6Hours: { ticksCount: 12, dataCountsDivider: 20 },
  last24Hours: { ticksCount: 24, dataCountsDivider: 6 },
  yesterday: { ticksCount: 24, dataCountsDivider: 6 },
  last3Days: { ticksCount: 3, dataCountsDivider: 48 },
  last7Days: { ticksCount: 7, dataCountsDivider: 24 },
  last30Days: { ticksCount: 30, dataCountsDivider: 6 },
  custom: { ticksCount: 0, dataCountsDivider: 6 },
}

type ChartSettingsType = typeof ChartSettings

const setDateParam = (startDate: Dayjs, endDate: Dayjs) => {
  const startDateTime = startDate
  const startDateString = startDateTime.format("YYYY-MM-DDTHH:mm:00+0000")
  const endDateString = endDate.format("YYYY-MM-DDTHH:mm:00+0000")

  return {
    startDateTime,
    startDateString,
    endDateString,
  }
}

const getIntervalInMinutesAndItemsCount = (value: string | number | boolean, daysCount: number = 0) => {
  if (value === "lastHour") {
    return { intervalInMinutes: 1, itemsCount: 60, settings: ChartSettings["lastHour"] }
  }

  if (value === "last6Hours") {
    return { intervalInMinutes: 3, itemsCount: 120, settings: ChartSettings["last6Hours"] }
  }

  if (["last24Hours", "yesterday"].includes(String(value)) || daysCount === 1) {
    return { intervalInMinutes: 10, itemsCount: 144, settings: ChartSettings["last24Hours"] }
  }

  if (value === "last3Days" || (daysCount <= 3 && daysCount > 0)) {
    return {
      intervalInMinutes: 30,
      itemsCount: 48 * (daysCount || 3),
      settings: { ...ChartSettings["last3Days"], ticksCount: daysCount || 3 },
    }
  }

  if (value === "last7Days" || (daysCount <= 10 && daysCount > 0)) {
    return {
      intervalInMinutes: 60,
      itemsCount: 24 * (daysCount || 7),
      settings: { ...ChartSettings["last7Days"], ticksCount: daysCount || 7 },
    }
  }

  if (value === "last30Days") {
    return { intervalInMinutes: 240, itemsCount: 180, settings: ChartSettings["last30Days"] }
  }

  if (daysCount <= 15) {
    return {
      intervalInMinutes: 120,
      itemsCount: 12 * (daysCount || 15),
      settings: {
        ticksCount: daysCount,
        dataCountsDivider: 12,
      },
    }
  }

  return {
    intervalInMinutes: 240,
    itemsCount: 6 * daysCount,
    settings: {
      ticksCount: daysCount,
      dataCountsDivider: 6,
    },
  }
}

const handleGetBarChartSettings = (value: string | number | boolean, daysCount: number = 0) => {
  if (value === "lastHour") {
    return getBarChartSettings().lastHour
  }

  if (value === "last6Hours") {
    return getBarChartSettings().last6Hours
  }

  if (value === "last24Hours") {
    return getBarChartSettings().last24Hours
  }

  if (value === "yesterday" || daysCount === 1) {
    return getBarChartSettings().yesterday
  }

  if (value === "last3Days" || (daysCount <= 3 && daysCount > 0)) {
    return { ...getBarChartSettings().last3Days, ticksCount: daysCount || 3 }
  }

  if (value === "last7Days" || (daysCount <= 10 && daysCount > 0)) {
    return { ...getBarChartSettings().last7Days, ticksCount: daysCount || 7 }
  }

  if (value === "last30Days") {
    return { ...getBarChartSettings().last30Days, ticksCount: daysCount || 30 }
  }

  if (daysCount <= 15) {
    return { ...getBarChartSettings().custom, ticksCount: daysCount, intervalItemsCount: 12 }
  }

  return { ...getBarChartSettings().custom, ticksCount: daysCount }
}

export const getMonitoringDateRangeParams = (
  value: string | number | boolean,
  dateRange: [Dayjs, Dayjs] | null,
  timeZone: string | undefined,
): {
  startDateTime: Dayjs
  startDateString: string
  endDateString: string
  intervalInMinutes: number
  itemsCount: number
  daysCount?: number
  settings: ChartSettingsType[keyof ChartSettingsType]
  barChartSettings: BarChartSettings
} => {
  const currentTime = timeZone ? dayjs().tz(timeZone || "UTC", true) : dayjs()

  if (value === "lastHour") {
    return {
      ...setDateParam(dayjs().utc().subtract(1, "hour"), dayjs().utc()),
      ...getIntervalInMinutesAndItemsCount(value),
      barChartSettings: handleGetBarChartSettings(value),
    }
  }

  if (value === "last6Hours") {
    const last6Hours = dayjs().utc().subtract(6, "hour")

    return {
      ...setDateParam(last6Hours, dayjs().utc()),
      ...getIntervalInMinutesAndItemsCount(value),
      barChartSettings: handleGetBarChartSettings(value),
    }
  }

  if (value === "last24Hours") {
    const last24Hours = dayjs().utc().subtract(24, "hour").utc()

    return {
      ...setDateParam(last24Hours, dayjs().utc()),
      ...getIntervalInMinutesAndItemsCount(value),
      barChartSettings: handleGetBarChartSettings(value),
    }
  }

  if (value === "yesterday") {
    const yesterdayStart = currentTime.subtract(1, "day").startOf("day").utc()
    const yesterdayEnd = yesterdayStart.add(23, "hours").add(59, "minutes").utc()

    return {
      ...setDateParam(yesterdayStart, yesterdayEnd),
      ...getIntervalInMinutesAndItemsCount(value),
      barChartSettings: handleGetBarChartSettings(value),
    }
  }

  if (value === "last3Days") {
    const last3DaysStart = currentTime.subtract(3, "day").startOf("day").utc()
    const last3DaysEnd = last3DaysStart.add(2, "day").add(23, "hours").add(59, "minutes").utc()

    return {
      ...setDateParam(last3DaysStart, last3DaysEnd),
      ...getIntervalInMinutesAndItemsCount(value),
      barChartSettings: handleGetBarChartSettings(value),
    }
  }

  if (value === "last7Days") {
    const last7DaysStart = currentTime.subtract(7, "day").startOf("day").utc()
    const last7DaysEnd = last7DaysStart.add(6, "day").add(23, "hours").add(59, "minutes").utc()
    return {
      ...setDateParam(last7DaysStart, last7DaysEnd),
      ...getIntervalInMinutesAndItemsCount(value),
      barChartSettings: handleGetBarChartSettings(value),
    }
  }

  if (value === "last30Days") {
    const last30DaysStart = currentTime.subtract(30, "day").startOf("day").utc()
    const last30DaysEnd = last30DaysStart.add(29, "day").add(23, "hours").add(59, "minutes").utc()

    return {
      ...setDateParam(last30DaysStart, last30DaysEnd),
      ...getIntervalInMinutesAndItemsCount(value),
      barChartSettings: handleGetBarChartSettings(value),
    }
  }

  const dateRangeStart = (dateRange?.[0] ?? currentTime).tz(timeZone || "UTC", true).utc()
  const dateRangeEnd = (dateRange?.[1] ?? currentTime).tz(timeZone || "UTC", true).utc()

  const daysCount = dateRangeEnd.diff(dateRange?.[0], "days") + 1

  return {
    startDateTime: dayjs(dateRangeStart.format("YYYY-MM-DDTHH:mm:00+0000")),
    endDateString: dateRangeEnd.add(23, "hours").add(59, "minutes").format("YYYY-MM-DDTHH:mm:00+0000"),
    startDateString: dateRangeStart.format("YYYY-MM-DDTHH:mm:00+0000"),
    ...getIntervalInMinutesAndItemsCount(value, daysCount),
    barChartSettings: handleGetBarChartSettings(value, daysCount),
  }
}
